/**
 * Attach an image to the Browse search: downscale, embed once, store the vector, re-run.
 *
 * Shared by the two ways in — the "Upload to search" button and dropping a file anywhere on
 * the search row (`dropzone.ts`) — so both behave identically and there is one place where
 * "what an image search does" is defined.
 *
 * The embedding goes through `search.vector`, the endpoint that already owns image embedding
 * for /labs/search. `k: 1` because its ranked results are thrown away here: only `returnVec`
 * is wanted, and that flag exists for exactly this "give me the query vector" use.
 */
import { api } from "../../api";
import { getAction } from "../../actionRegistry";
import { setSearchActive } from "../../globalState";
import { toastError } from "../../ui/messages";
import { downscaleForEmbedding, vecToB64 } from "./downscale";
import { setSearchImage } from "./store";

/** True when the search ran; false when the file was rejected or the embed failed (the
 *  caller only needs this to clear its own busy state — the user is told by a toast). */
export async function runImageSearch(file: File): Promise<boolean> {
  if (!file.type.startsWith("image/")) {
    toastError("That is not an image file");
    return false;
  }
  try {
    const { b64, preview } = await downscaleForEmbedding(file);
    const res = await api.search.vector({ imageB64: b64, k: 1, returnVec: true });
    if (!res.queryVec) throw new Error("The image could not be read");
    setSearchImage({ name: file.name, preview, vecB64: vecToB64(res.queryVec) });
    // A search with an image but an empty box is still a search. `search()` only runs its
    // empty-box branch when a search is already active, so say so before asking.
    setSearchActive(1);
    // `search`, not `searchtimed` — the same call and the same reason as `seedFromUrl.ts`.
    // There is nothing to debounce: this fires once, when a file lands, not per keystroke,
    // and the second it would have waited comes AFTER a downscale and an embed round trip,
    // which is the worst possible place to add dead time. It is also what opens the search
    // interface when the image is dropped on the front page rather than on Browse.
    getAction("search")?.();
    return true;
  } catch (err: unknown) {
    toastError(err instanceof Error ? err.message : "Image search failed");
    return false;
  }
}
